import { useEffect, useRef } from "react";
import ReactEcharts, { EChartsOption } from "echarts-for-react";
import { useAppSelector } from "../../../../../app/hooks";
import { tooltipMark } from "../../../shared/reportUtil";
import { WriterEssayDraftHistoryViewModel } from "../../../shared/progress/essayDraftHistory/essayDraftHistoryReportApiSlice";
import {
	getSeriesData,
	getXAxisFromMaxDraftCount,
	updateOptions,
} from "../../../shared/progress/essayDraftHistory/ChartUtil";

interface EssayScoresByDraftChartProps {
	data: WriterEssayDraftHistoryViewModel[];
	isConstructedResponse: boolean;
}

export default function EssayScoresByDraftChart({
	data,
	isConstructedResponse,
}: EssayScoresByDraftChartProps) {
	const { foregroundColor, chartPalette, accentColor } = useAppSelector(
		(state) => state.contrast
	);

	const initialEChartOptions: EChartsOption = {
		textStyle: {
			color: foregroundColor,
			fontFamily: "Montserrat",
			fontWeight: "500",
		},
		grid: {
			containLabel: true,
			right: 40,
			left: 40,
			top: 60,
			bottom: 25,
		},
		xAxis: {
			type: "category",
			data: getXAxisFromMaxDraftCount(data),
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
		},
		yAxis: {
			type: "value",
			min: 0,
			max: isConstructedResponse ? 3 : 30,
			interval: isConstructedResponse ? 1 : 5,
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			name: "Draft Score",
			nameLocation: "middle",
			nameGap: 40,
			splitLine: {
				show: true,
				lineStyle: {
					color: accentColor,
				},
			},
		},
		tooltip: {
			trigger: "axis",
			formatter: (
				params: {
					color: string;
					value: string;
				}[]
			) => {
				if (!params.find((p) => p.value)) return;
				return params.reduce(
					(a: string, v: any) =>
						a + `<div>${tooltipMark(v.color)} Draft Score: <b>${v.value}</b></div>`,
					""
				);
			},
			textStyle: {
				fontSize: 12,
			},
		},
		animationDurationUpdate: 500,
		series: [],
	};

	const chartRef = useRef<ReactEcharts>(null);

	useEffect(() => {
		const options = updateOptions(initialEChartOptions, getSeriesData(data, foregroundColor));

		if (chartPalette != undefined) {
			options.color = chartPalette;
		}

		chartRef.current?.getEchartsInstance().setOption(options);
	}, [data]);

	return (
		<div className="mt-8 p-4 border-2 border-gray-200">
			{data.length > 0 ? (
				<ReactEcharts
					ref={chartRef}
					option={initialEChartOptions}
					className="w-full !h-96"
				/>
			) : (
				<div className="w-full h-96 border-2 border-gray-200 flex items-center justify-center bg-gray-100">
					<p>No scores to display</p>
				</div>
			)}
		</div>
	);
}
